import { action, computed, observable } from 'mobx';
import React from 'react';
import { GENERAL } from '../../constants/Errors';
import Config from '../../domain/entities/Config';
import NotificationsCollection from '../../domain/entities/Notification/NotificationsCollection';
import Transaction from '../../domain/entities/Transaction/Transaction';
import ConfigRepository from '../../repositories/ConfigRepository';
import GiftWrapRepository from '../../repositories/GiftWrapRepository';
import TransactionRepository from '../../repositories/TransactionRepository';
import { IGiftWrapSkuItem } from '../../api/IApiResponses';
import { GiftWrapSettings } from '../../api/IDataMappings';
import { NOTIFICATION_GROUP, NOTIFICATION_TYPE } from '../../constants/Notifications';
import SessionRepository from '../../repositories/SessionRepository/SessionRepository';

export interface IGiftWrapControllerConfig {
  configRepository: ConfigRepository;
  transactionRepository: TransactionRepository;
  giftWrapRepository: GiftWrapRepository;
  notificationsCollection: NotificationsCollection;
  sessionRepository: SessionRepository;
}

export interface IGiftWrapScreenViewControllerData {
  isExpanded: boolean;
  isGiftWrapImageHidden: boolean;
  isGWImageHiddenInShoppingBag: boolean;
  isGiftWrapSkusPriceSame: boolean;
  isMessageExpanded: boolean;
  isMultipleGiftWrapEnabled: boolean;
  selectedGiftWrapItemSku: string;
  giftWrapSkus: IGiftWrapSkuItem[];
  message: string;
  cardMessage: string;
  isGiftWrapMessageLoading: boolean;
  config: Config;
  transaction: Transaction;
}

export default class GiftWrapViewController {

  @observable isExpanded = false;

  @observable isMessageExpanded = false;

  @observable isLoading = false;

  @observable isMessageLoading = false;

  private configRepository: ConfigRepository;

  private giftWrapRepository: GiftWrapRepository;

  private transactionRepository: TransactionRepository;

  private notificationsCollection: NotificationsCollection;

  private sessionRepository: SessionRepository;

  constructor(config: IGiftWrapControllerConfig) {
    this.configRepository = config.configRepository;
    this.giftWrapRepository = config.giftWrapRepository;
    this.transactionRepository = config.transactionRepository;
    this.notificationsCollection = config.notificationsCollection;
    this.sessionRepository = config.sessionRepository;
  }

  @action loaded(): void {

    if (!this.transactionRepository.transaction.giftWrapSettings.giftWrapEnabled && this.transactionRepository.transaction.giftMessageSettings.giftMessageEnabled) {
      this.isMessageExpanded = true;
    }

    if (this.transactionRepository?.transaction?.giftWrap) {
      this.isExpanded = true;
    }

    if (this.transactionRepository?.transaction?.cardMessage !== '') {
      this.giftWrapRepository.message = this.transactionRepository?.transaction?.cardMessage;
      this.isMessageExpanded = true;
    }

    const { isDefaultGWSelected, giftWrapSelectedSku } = this.giftWrapSettings;
    if (!this.sessionRepository.isGiftWrapOptedOut() && isDefaultGWSelected && !giftWrapSelectedSku) {
      const skuBaseId = this.giftWrapSettings?.giftWrapSkus[0].sku_base_id;
      this.addGiftWrapRadio(skuBaseId);
    }
  }

  @action
  public toggleGiftWrapSelected = async (): Promise<void> => {
    const shouldAddGiftWrap = !this.transactionRepository?.transaction?.giftWrap && !this.isExpanded;
    const isCardMessageEmpty = this.transactionRepository?.transaction.cardMessage === '';
    this.sessionRepository.setGiftWrapOptedOutFlag();
    this.giftWrapRepository.setIsGiftWrapLoading(true);
    try {
      if (shouldAddGiftWrap) {
        await this.giftWrapRepository.setGiftWrap();
        this.isExpanded = true;
      } else {
        if (!isCardMessageEmpty && this.isExpanded) {
          await this.giftWrapRepository.removeGiftWrapAndGiftWrapMessage();
          this.isExpanded = false;
        } else {
          await this.giftWrapRepository.removeGiftWrap();
          this.isExpanded = false;
        }
      }
    } catch (error) {
      this.notificationsCollection.addNotification({
        group: NOTIFICATION_GROUP.GIFT_WRAP,
        key: GENERAL.GENERIC,
        type: NOTIFICATION_TYPE.DANGER,
      });
      this.isExpanded = false;
    } finally {
      this.giftWrapRepository.setIsGiftWrapLoading(false);
    }
  };

  @action
  public toggleGiftWrapMessage = async (): Promise<void> => {
    
    this.isMessageExpanded = !this.isMessageExpanded;
    const shouldRemoveGiftWrapMessage = !this.isMessageExpanded && this?.giftWrapRepository?.message !== '' && this.transactionRepository?.transaction?.cardMessage !== '';

    if (!this.isMessageExpanded) {
      this.giftWrapRepository.message = '';
      
      this.notificationsCollection.clearNotifications(NOTIFICATION_GROUP.GIFT_WRAP_MESSAGE);
    }
    
    if (shouldRemoveGiftWrapMessage) {
      await this.giftWrapRepository.removeGiftWrapMessage();
      await this.transactionRepository.loadTransaction();
    }
  };

  @action
  public handleMessageChange =  (e: React.FormEvent<HTMLTextAreaElement>): void => {
    e.preventDefault();
    this.giftWrapRepository.handleMessageChange(e);
  };

  @computed
  private get giftWrapSettings(): GiftWrapSettings {
    return this.transactionRepository?.transaction?.giftWrapSettings;
  }

  @computed
  private get isGiftWrapSkusPriceSame(): boolean {
    const giftWrapSkuItems = this.giftWrapSettings?.giftWrapSkus;
    if (!this.giftWrapSettings?.isGiftWrapMultipleEnabled) {
      return true;
    }
    return (giftWrapSkuItems ? giftWrapSkuItems.every(skuItem => skuItem.price === giftWrapSkuItems[0].price) : null);
  }

  @action
  public addGiftWrapRadio = async (skuId: string): Promise<void> => {
    this.giftWrapRepository.setIsGiftWrapLoading(true);
    try {
      await this.giftWrapRepository.setGiftWrapSku(skuId);
      this.isExpanded = true;
    } catch (error) {
      this.notificationsCollection.addNotification({
        group: NOTIFICATION_GROUP.GIFT_WRAP,
        key: GENERAL.GENERIC,
        type: NOTIFICATION_TYPE.DANGER,
      });
      this.isExpanded = false;
    } finally {
      this.giftWrapRepository.setIsGiftWrapLoading(false);
    }
  };

  @computed
  get data(): IGiftWrapScreenViewControllerData {
    return {
      cardMessage: this.transactionRepository?.transaction?.cardMessage,
      config: this.configRepository.config,
      giftWrapSkus: this.giftWrapSettings?.giftWrapSkus,
      isExpanded: this.isExpanded,
      isGWImageHiddenInShoppingBag: this.giftWrapSettings?.isGWImageHiddenInShoppingBag,
      isGiftWrapImageHidden: this.giftWrapSettings?.isGiftWrapImageHidden,
      isGiftWrapMessageLoading: this.giftWrapRepository.isGiftMessageLoading,
      isGiftWrapSkusPriceSame: this.isGiftWrapSkusPriceSame,
      isMessageExpanded: this.isMessageExpanded,
      isMultipleGiftWrapEnabled: this.giftWrapSettings?.isGiftWrapMultipleEnabled,
      message: this.giftWrapRepository.message,
      selectedGiftWrapItemSku: this.giftWrapSettings?.giftWrapSelectedSku,
      transaction: this.transactionRepository.transaction,
    };
  }
}